/**
 * EnrollmentsSection
 *
 * Per-learner enrollment list — covers both **courses and learning
 * paths**. Split into In Progress vs Completed tabs with per-row
 * progress bars and a "Send Reminder" action on stalled rows.
 *
 * Progress for learning paths is rolled up server-side and surfaced
 * via the same `progressPercentage` field as standalone courses, so
 * the row UI is uniform across content types.
 *
 * Data: server-paged `enrollmentsClient.searchUserEnrollments`. We
 * fetch a single page sized to fit the typical learner's enrollment
 * count and split client-side, so the tab toggle is instant and the
 * count badges are accurate.
 */

import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Certificate, CourseEnrollment, EnrollmentStatus } from '../../apis/api-client';
import { certificatesClient, coursesClient, enrollmentsClient, reportsClient } from '../../apis/client-instances';
import { alert, getFileUrl } from '../../hooks/function';
import {
  formatDate,
  getAssignmentStatusColor,
  getAssignmentStatusLabel,
  getEnrollmentStatusLabel,
} from '../../utils/roleHelpers';
import { BlockIcon, CheckIcon, DeleteIcon, DownloadIcon, EmailIcon, MoreVertIcon, PlayIcon, RefreshIcon } from '../icons';
import { Box, Chip, CircularProgress, IconButton, LinearProgress, ListItemIcon, ListItemText, Menu, MenuItem, Tab, Tabs, Tooltip, Typography } from '../ui';
import PagedTable, { PagedTableColumn } from '../common/PagedTable';
import { usePagedList } from '../../hooks/usePagedList';
import { activityStatusPalette } from '../../utils/reportingHelpers';
import ConfirmDialog from '../ConfirmDialog';

interface EnrollmentsSectionProps {
  userId: number;
  isAdmin?: boolean;
}

export default function EnrollmentsSection({ userId, isAdmin }: EnrollmentsSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  // Track in-flight reminder calls per enrollmentId so we can show a
  // spinner on the right button rather than blocking the whole table.
  const [remindingId, setRemindingId] = useState<number | null>(null);
  const [tab, setTab] = useState<'in-progress' | 'completed'>('in-progress');
  // Per-row kebab menu — same pattern used on the Workspaces/Groups
  // tables. Anchor + row are tracked together so the menu items can
  // act on the right enrollment without prop-drilling through the
  // column render.
  const [rowMenu, setRowMenu] = useState<{
    anchorEl: HTMLElement;
    row: CourseEnrollment;
  } | null>(null);
  const closeRowMenu = () => setRowMenu(null);
  // Destructive-action confirm targets. Revoke (in-progress) and
  // delete (completed) both route through ConfirmDialog with a typed
  // confirm safeguard.
  const [revokeTarget, setRevokeTarget] = useState<CourseEnrollment | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CourseEnrollment | null>(null);

  const paged = usePagedList<CourseEnrollment>({
    fetcher: useCallback(
      async ({ page, pageSize, search }) => {
        const result = await enrollmentsClient.searchUserEnrollments(
          userId,
          search || undefined,
          page,
          pageSize,
        );
        return {
          items: result.items ?? [],
          totalCount: result.totalCount ?? 0,
          page: result.page ?? page,
          pageSize: result.pageSize ?? pageSize,
        };
      },
      [userId],
    ),
    deps: [userId],
    initialPageSize: 100,
    onError: () => alert(enqueueSnackbar, 'Failed to load enrollments', 'error'),
  });

  // Split loaded rows by enrollment status. "In progress" sweeps
  // anything that isn't Completed — including NotStarted — so stalled
  // never-started enrollments get caught by the reminder flow.
  const { inProgressRows, completedRows } = useMemo(() => {
    const inProgress: CourseEnrollment[] = [];
    const completed: CourseEnrollment[] = [];
    for (const e of paged.items) {
      if (e.enrollmentStatus === EnrollmentStatus.Completed) completed.push(e);
      else inProgress.push(e);
    }
    return { inProgressRows: inProgress, completedRows: completed };
  }, [paged.items]);

  const rows = tab === 'in-progress' ? inProgressRows : completedRows;

  const openCourse = async (enrollment: CourseEnrollment) => {
    if (!enrollment.courseId) return;
    try {
      const course = await coursesClient.getCourseById(enrollment.courseId);
      if (course?.slug) navigate(`/courses/${course.slug}`);
    } catch {
      alert(enqueueSnackbar, 'Failed to open course', 'error');
    }
  };

  // Fire a manual reminder for the assigned course. When the row
  // carries an `assignmentDefinitionId`, the reminder is course-
  // scoped; otherwise it's a general nudge to the learner's queue.
  const handleSendReminder = async (enrollment: CourseEnrollment) => {
    const enrollmentId = enrollment.enrollmentId ?? enrollment.id ?? 0;
    setRemindingId(enrollmentId);
    try {
      await reportsClient.sendReminder({
        userId,
        assignmentDefinitionId: enrollment.assignmentDefinitionId,
      });
      alert(
        enqueueSnackbar,
        `Reminder sent for ${enrollment.courseTitle || 'this course'}.`,
        'success',
      );
    } catch (err: any) {
      alert(
        enqueueSnackbar,
        err?.response?.data || 'Failed to send reminder',
        'error',
      );
    } finally {
      setRemindingId(null);
    }
  };

  /**
   * Admin override: mark an in-progress enrollment complete regardless
   * of course type. Uses the dedicated `adminCompleteCourse` endpoint,
   * which shares the same completion side effects as the learner paths
   * (create enrollment if missing, set completedAt + 100%, cascade to
   * learning paths, generate certificate) but is admin-only and scoped
   * to the caller's chain on the server. Kept distinct from the
   * learner-facing external-event completion so a learner can't
   * self-complete an internal course through this surface.
   */
  const handleMarkComplete = async (enrollment: CourseEnrollment) => {
    if (!enrollment.courseId) return;
    try {
      await enrollmentsClient.adminCompleteCourse({
        userId,
        courseId: enrollment.courseId,
      });
      alert(
        enqueueSnackbar,
        `Marked "${enrollment.courseTitle || 'course'}" complete.`,
        'success',
      );
      paged.refresh();
    } catch (err: any) {
      alert(
        enqueueSnackbar,
        err?.response?.data || 'Failed to mark course complete',
        'error',
      );
    }
  };

  /**
   * Revoke a learner's access to an in-progress course. Backed by the
   * dedicated `revokeEnrollment` endpoint — the enrollment row stays
   * for audit but the learner loses access. Deferred behind a typed
   * confirm so a misclick can't kick someone out of training.
   */
  const handleRevokeConfirmed = async () => {
    if (!revokeTarget) return;
    const enrollmentId = revokeTarget.enrollmentId ?? revokeTarget.id;
    if (!enrollmentId) return;
    const target = revokeTarget;
    setRevokeTarget(null);
    try {
      await enrollmentsClient.revokeEnrollment(enrollmentId);
      alert(
        enqueueSnackbar,
        `Revoked access to "${target.courseTitle || 'course'}".`,
        'success',
      );
      paged.refresh();
    } catch (err: any) {
      alert(
        enqueueSnackbar,
        err?.response?.data || 'Failed to revoke access',
        'error',
      );
    }
  };

  /**
   * Flip a completed enrollment back to "in progress." Uses
   * `startCourse` — the same endpoint the learner hits when they
   * resume a course, which on the backend resets the enrollment's
   * status (and clears `completedAt` when the row was previously
   * completed). Distinct from Reassign: this MUTATES the existing
   * row rather than creating a new one, so the original completion
   * date and certificate effectively disappear from the row's
   * history. Useful when an admin completed someone by mistake.
   */
  const handleMarkInProgress = async (enrollment: CourseEnrollment) => {
    if (!enrollment.courseId) return;
    try {
      await enrollmentsClient.startCourse({
        userId,
        courseId: enrollment.courseId,
      });
      alert(
        enqueueSnackbar,
        `Moved "${enrollment.courseTitle || 'course'}" back to in progress.`,
        'success',
      );
      paged.refresh();
    } catch (err: any) {
      alert(
        enqueueSnackbar,
        err?.response?.data || 'Failed to move enrollment back to in progress',
        'error',
      );
    }
  };

  /**
   * Re-enroll a learner in a completed course so they can retake it.
   * Creates a fresh CourseEnrollment row — the original completed row
   * stays in the learner's history (CE credit, certificate). This is
   * how "Reassign" works on the Completed tab.
   */
  const handleReassign = async (enrollment: CourseEnrollment) => {
    if (!enrollment.courseId) return;
    try {
      await enrollmentsClient.createEnrollment({
        userId,
        courseId: enrollment.courseId,
      });
      alert(
        enqueueSnackbar,
        `Re-enrolled in "${enrollment.courseTitle || 'course'}".`,
        'success',
      );
      paged.refresh();
    } catch (err: any) {
      alert(
        enqueueSnackbar,
        err?.response?.data || 'Failed to reassign',
        'error',
      );
    }
  };

  /**
   * Delete a completed enrollment from the learner's record. No
   * hard-delete endpoint exists yet — `revokeEnrollment` is the
   * closest semantic neighbor (removes the row's access + audit
   * stays). Once the backend ships a `deleteEnrollment`, swap here.
   * Same typed-confirm safeguard as Revoke.
   */
  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    const enrollmentId = deleteTarget.enrollmentId ?? deleteTarget.id;
    if (!enrollmentId) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await enrollmentsClient.revokeEnrollment(enrollmentId);
      alert(
        enqueueSnackbar,
        `Deleted enrollment for "${target.courseTitle || 'course'}".`,
        'success',
      );
      paged.refresh();
    } catch (err: any) {
      alert(
        enqueueSnackbar,
        err?.response?.data || 'Failed to delete enrollment',
        'error',
      );
    }
  };

  /**
   * "View certificate" — open the rendered cert image in a new browser
   * tab. Admins get the full canvas (template background + name +
   * course title overlay) at native resolution rather than the
   * cropped/scaled download blob. Browser tab's built-in Save action
   * still gives them a download path when they want one.
   *
   * Resolves the SAS-signed URL via `getFileUrl` — the same path the
   * download flow used — then hands it to `window.open`. Generates
   * the certificate on demand if the enrollment doesn't have one yet.
   */
  const handleViewCertificate = async (enrollment: CourseEnrollment) => {
    const enrollmentId = enrollment.enrollmentId ?? enrollment.id;
    if (!enrollmentId) return;
    // Pop the tab synchronously BEFORE the network calls so the
    // browser doesn't kill it as a popup. We seed it with a tiny
    // placeholder and rewrite the location once the URL resolves.
    const previewTab = window.open('', '_blank', 'noopener,noreferrer');
    setDownloadingId(enrollmentId);
    try {
      // The lookup 404s when no certificate exists yet — that's the normal
      // "not generated" case, so swallow it to null and fall through to
      // generation (rather than letting the throw abort the flow).
      let cert: Certificate | null = null;
      try {
        cert = await certificatesClient.getCertificateByEnrollment(enrollmentId);
      } catch {
        /* none yet */
      }
      if (!cert) {
        try {
          cert = await certificatesClient.generateCertificate(enrollmentId);
        } catch {
          /* generation failed (e.g. no template configured) */
        }
      }
      if (!cert) {
        previewTab?.close();
        alert(enqueueSnackbar, 'Could not generate certificate. Ensure a certificate template is configured.', 'warning');
        return;
      }
      const url = await getFileUrl(cert.imageFilename);
      if (!url) {
        previewTab?.close();
        alert(enqueueSnackbar, 'Certificate file URL could not be resolved.', 'error');
        return;
      }
      if (previewTab) {
        previewTab.location.href = url;
      } else {
        // Popup blocker swallowed the new tab — fall back to opening
        // in the current window. Better than a silent no-op.
        window.location.href = url;
      }
    } catch {
      previewTab?.close();
      alert(enqueueSnackbar, 'Failed to open certificate', 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  // Build the columns based on the active tab. The right-side action
  // column changes — Send Reminder in progress, Download certificate
  // when completed — so the action affordance stays in the same slot
  // and rows don't visually shift between tabs.
  const columns: PagedTableColumn<CourseEnrollment>[] = [
    {
      key: 'course',
      label: 'Title',
      render: (e) => (
        <Typography variant="body2" fontWeight={500}>
          {e.courseTitle || `Course #${e.courseId}`}
        </Typography>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (e) => {
        // Pastel palette via reportingHelpers — same family used on
        // the ActivityReport status chip, so user-page and report-page
        // pills read as one visual system. Replaces the bright MUI
        // primary blue that used to wash out the rest of the row.
        const palette = activityStatusPalette(e.enrollmentStatus ?? null);
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={getEnrollmentStatusLabel(e.enrollmentStatus)}
              size="small"
              sx={{
                bgcolor: palette.bgcolor,
                color: palette.color,
                fontWeight: 600,
                border: 'none',
              }}
            />
            {e.isAssigned && e.assignmentStatus && (
              <Chip
                label={getAssignmentStatusLabel(e.assignmentStatus)}
                color={getAssignmentStatusColor(e.assignmentStatus)}
                size="small"
              />
            )}
          </Box>
        );
      },
    },
    {
      key: 'progress',
      label: 'Progress',
      width: 160,
      render: (e) => {
        // `progressPercentage` is the server-computed completion. For
        // learning paths the API rolls up across the path's courses
        // (server-side), so the same field works for both content types.
        const isCompleted = e.enrollmentStatus === EnrollmentStatus.Completed;
        const pct = Math.round(Math.max(0, Math.min(100, e.progressPercentage ?? (isCompleted ? 100 : 0))));
        return (
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {isCompleted ? 'Complete' : 'In progress'}
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color={isCompleted ? 'success.main' : 'primary.main'}
              >
                {pct}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 6,
                borderRadius: 999,
                bgcolor: 'grey.100',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  ...(isCompleted
                    ? { bgcolor: 'success.main' }
                    : {
                        background:
                          'var(--brand-gradient)',
                      }),
                },
              }}
            />
          </Box>
        );
      },
    },
    { key: 'deadline', label: 'Deadline', render: (e) => formatDate(e.deadline) },
    { key: 'startedAt', label: 'Started', render: (e) => formatDate(e.startedAt) },
    { key: 'lastAccessedAt', label: 'Last Accessed', render: (e) => formatDate(e.lastAccessedAt) },
    ...(tab === 'completed'
      ? [{
          key: 'completedAt',
          label: 'Completed',
          render: (e: CourseEnrollment) => formatDate(e.completedAt),
        }]
      : []),
    // Single kebab affordance per row — same pattern used on the
    // Groups and Workspaces tables. The menu items change by tab,
    // but the column slot stays in the same spot so rows don't
    // visually shift when switching In Progress ↔ Completed.
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      width: 80,
      render: (e) => {
        // Learners viewing their own profile don't get admin actions.
        if (!isAdmin) return null;
        const enrollmentId = e.enrollmentId ?? e.id;
        const isBusyHere = remindingId === enrollmentId || downloadingId === enrollmentId;
        return (
          <Box onClick={(ev) => ev.stopPropagation()} sx={{ display: 'inline-flex' }}>
            <Tooltip title="Actions">
              <span>
                <IconButton
                  size="small"
                  onClick={(ev) => setRowMenu({ anchorEl: ev.currentTarget, row: e })}
                  disabled={isBusyHere}
                >
                  {isBusyHere ? <CircularProgress size={16} /> : <MoreVertIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight={700}>
          Enrollments
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {inProgressRows.length} in progress · {completedRows.length} completed
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
          <Tab
            value="in-progress"
            label={`In Progress (${inProgressRows.length})`}
          />
          <Tab
            value="completed"
            label={`Completed (${completedRows.length})`}
          />
        </Tabs>
      </Box>

      <PagedTable<CourseEnrollment>
        columns={columns}
        rows={rows}
        // Total reflects the *currently shown* tab so paging at the
        // bottom matches what's on screen.
        totalCount={rows.length}
        page={1}
        pageSize={rows.length || 1}
        loading={paged.loading}
        rowKey={(e) => e.id ?? `${e.userId}-${e.courseId}`}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        onRowClick={openCourse}
        emptyMessage={
          tab === 'in-progress' ? 'No in-progress enrollments.' : 'No completed enrollments yet.'
        }
      />

      {/* Per-row kebab menu. Items vary by tab. Closing on outside
          click; each item closes itself before firing so a slow
          action doesn't leave the menu open against a refreshing
          table. */}
      <Menu
        anchorEl={rowMenu?.anchorEl ?? null}
        open={!!rowMenu}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {tab === 'in-progress' ? (
          [
            <MenuItem
              key="remind"
              onClick={() => {
                if (rowMenu) handleSendReminder(rowMenu.row);
                closeRowMenu();
              }}
            >
              <ListItemIcon>
                <EmailIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Send reminder</ListItemText>
            </MenuItem>,
            <MenuItem
              key="complete"
              onClick={() => {
                if (rowMenu) handleMarkComplete(rowMenu.row);
                closeRowMenu();
              }}
            >
              <ListItemIcon>
                <CheckIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Mark as complete</ListItemText>
            </MenuItem>,
            <MenuItem
              key="revoke"
              onClick={() => {
                if (rowMenu) setRevokeTarget(rowMenu.row);
                closeRowMenu();
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>
                <BlockIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Revoke access</ListItemText>
            </MenuItem>,
          ]
        ) : (
          [
            <MenuItem
              key="cert"
              onClick={() => {
                if (rowMenu) handleViewCertificate(rowMenu.row);
                closeRowMenu();
              }}
            >
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>View certificate</ListItemText>
            </MenuItem>,
            <MenuItem
              key="mark-in-progress"
              onClick={() => {
                if (rowMenu) handleMarkInProgress(rowMenu.row);
                closeRowMenu();
              }}
            >
              <ListItemIcon>
                <PlayIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Mark in progress</ListItemText>
            </MenuItem>,
            <MenuItem
              key="reassign"
              onClick={() => {
                if (rowMenu) handleReassign(rowMenu.row);
                closeRowMenu();
              }}
            >
              <ListItemIcon>
                <RefreshIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Reassign</ListItemText>
            </MenuItem>,
            <MenuItem
              key="delete"
              onClick={() => {
                if (rowMenu) setDeleteTarget(rowMenu.row);
                closeRowMenu();
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>,
          ]
        )}
      </Menu>

      {/* Revoke access — typed-confirm safeguard. Backed by
          `revokeEnrollment`. Same protection used on group + content
          deletes elsewhere in the admin shell. */}
      <ConfirmDialog
        open={!!revokeTarget}
        title="Revoke access"
        content={`Revoke "${revokeTarget?.courseTitle || 'this course'}" for this learner? They'll lose access; their progress row stays for audit.`}
        confirmText="REVOKE"
        onConfirm={handleRevokeConfirmed}
        onCancel={() => setRevokeTarget(null)}
      />

      {/* Delete completed enrollment — same typed-confirm pattern. */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete enrollment"
        content={`Delete the completed enrollment for "${deleteTarget?.courseTitle || 'this course'}"? The certificate (if generated) stays in the certificates list.`}
        confirmText="DELETE"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
